import { XsI18nPipe } from '@nx-fe/pipes';
import { IDeviceModel, IMCConnectionInfo } from '@nx-fe/services';
import {
  ISabhaCallParticipant,
  ISabhaMediaStream,
  ISabhaMediaStreamTrack,
  SabhaCall,
  SabhaCallStatus,
} from '@soti/sabha-components';
import { Observable, EMPTY } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { SabhaDialogData, SabhaDialogType } from './device-chat/dialog/dialog-component/dialog.component';
import { SabhaDialogConfig } from './device-chat/dialog/dialog-config';
import { XSightRoom } from './models/xsight-room/xsight-room';

export function isRunning(call?: SabhaCall): boolean {
  return call && (isInProgress(call) || IsOnHold(call));
}

export function isInProgress(call?: SabhaCall): boolean {
  return call?.callStatus === SabhaCallStatus.InProgress;
}

export function IsOnHold(call?: SabhaCall): boolean {
  return call?.callStatus === SabhaCallStatus.OnHold;
}

export function ellipsize(text: string, length: number) {
  if (!text) return '';
  if (text.length > length) {
    return text.substring(0, length) + "...";
  }
  return text;
}

export function hasPresenterVideo(call?: SabhaCall): Observable<boolean> {
  return call?.presenter$.pipe(
    switchMap((presenter: ISabhaCallParticipant | null) => presenter && presenter.getMedia$() || EMPTY),
    mergeMap((media: ISabhaMediaStream<ISabhaMediaStreamTrack>) => media.getVideoTracks()),
    switchMap((track: ISabhaMediaStreamTrack) => track.muted$),
    map((isMuted: boolean) => !isMuted)
  );
}

export function getEndSessionDialogConfig(room: XSightRoom, dialogCloseTriggered$: Observable<any>): SabhaDialogConfig<SabhaDialogData> {
  const dialogConfig = new SabhaDialogConfig();
  dialogConfig.width = '312px';
  dialogConfig.disableClose = true;
  dialogConfig.autoFocus = true;
  dialogConfig.dialogCloseTriggered$ = dialogCloseTriggered$;
  dialogConfig.data = {
    titleToken: 'live_support_action_end_session',
    titleLowercase: true,
    titleTooltip: room.name,
    bodyToken: 'live_support_dialog_end_session_body',
    buttonTextToken: 'live_support_dialog_end_session_button',
    buttonColor: 'red',
    dialogType: SabhaDialogType.Alert,
  } as SabhaDialogData;

  return dialogConfig;
}

export function getExistingCallDialogConfig(deviceName: string, dialogCloseTriggered$: Observable<any>): SabhaDialogConfig<SabhaDialogData> {
  const dialogConfig = new SabhaDialogConfig();
  dialogConfig.width = '312px';
  dialogConfig.disableClose = true;
  dialogConfig.autoFocus = false;
  dialogConfig.dialogCloseTriggered$ = dialogCloseTriggered$;
  dialogConfig.data = {
    titleToken: 'live_support_existing_call_prompt_title',
    titleLowercase: true,
    titleIcon: 'end-session',
    bodyToken: 'live_support_existing_call_prompt_body',
    bodyParam: [ deviceName ],
    buttonTextToken: 'live_support_existing_call_prompt_button',
    buttonColor: 'red',
    dialogType: SabhaDialogType.Alert,
  } as SabhaDialogData;

  return dialogConfig;
}

export function formatTimeHHMMSS(timeInMs: number): string {
  const seconds = timeInMs / 1000;
  const s = Math.round(seconds % 60);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);

  return [
    h > 9 || h === 0 ? h : ('0' + h),
    m > 9 ? m : ('0' + m),
    s > 9 ? s : '0' + s
  ].filter(Boolean).join(':');
}

export function getMcInstanceCaption(
  device: IDeviceModel,
  mcConnections: IMCConnectionInfo[],
  xsI18nPipe: XsI18nPipe
): string {
  const defaultMcConnectionId = mcConnections.find(mcConnection => mcConnection.isDefault)?.id;
  const deviceMcConnection = mcConnections.find(mcConnection => mcConnection.id === device.mcConnectionId);
  return device.mcConnectionId === defaultMcConnectionId
    ? xsI18nPipe.transform('associateDevices_Default_Instance_Caption')
    : deviceMcConnection?.connectionName;
}
